import publicConfig from "../public-config.js";

import { request as httpRequest } from "./http-library.js";

const host = publicConfig.external_address;

// public

const Payments = {
  /**
   * GET Account Link for Stripe Hosted Onboarding Flow
   *
   * @function
   * @name getStripeAccountLink
   * @param {Function} - Callback
   */

  getStripeAccountLink: (accountLinkType, accessToken, cb) => {
    const requestParams = {
      host,
      path: `/v2/payments/users/me/accounts/${accountLinkType}/link`,
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    };

    httpRequest(requestParams, (res) => cb(res), null);
  },

  /**
   * GET Public Stripe Key Type
   *
   * @function
   * @name getStripePublicKeyType
   * @param {Function} - Callback
   */

  getStripePublicKeyType: (ssoId, accessToken, cb) => {
    const requestParams = {
      host,
      path: `/v2/payments/users/${ssoId}/accounts/gateway`,
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    };

    httpRequest(requestParams, (res) => cb(res), null);
  },

  /**
   * GET Managed Account Stripe Data
   *
   * @function
   * @name getManagedAccountData
   * @param {Function} - Callback
   */

  getManagedAccountData: (ssoid, accessToken, cb) => {
    const requestParams = {
      host,
      path: `/v2/payments/users/${ssoid}/accounts`,
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    };

    httpRequest(requestParams, (res) => cb(res), null);
  },

  /**
   * POST / PUT Managed Account Stripe Data
   *
   * @function
   * @name saveManagedAccountData
   * @param {Function} - Callback
   */

  saveManagedAccountData: (ssoid, accessToken, payload, isUpdate, cb) => {
    const requestParams = {
      host,
      path: `/v2/payments/users/${ssoid}/accounts`,
      method: isUpdate ? "PUT" : "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    };

    httpRequest(requestParams, (res) => cb(res), payload);
  },

  /**
   * POST Migrate Stripe Account to Managed Account
   *
   * @function
   * @name migrateRecipientAccount
   */

  migrateRecipientAccount: (ssoid, accessToken, cb) => {
    const requestParams = {
      host,
      path: `/v2/payments/users/${ssoid}/accounts/migrate`,
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    };

    httpRequest(requestParams, (res) => cb(res), { tos_accepted: true });
  },

  /**
   * GET Recipient Stripe Data
   *
   * @function
   * @name getRecipientData
   * @param {Function} - Callback
   */

  getRecipientData: (ssoid, accessToken, cb) => {
    const requestParams = {
      host,
      path: `/v2/payments/users/${ssoid}/recipients`,
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    };

    httpRequest(requestParams, (res) => cb(res), null);
  },

  /**
   * GET Customer Stripe Data
   *
   * @function
   * @name getCustomerData
   * @param {Function} - Callback
   */

  getCustomerData: (ssoid, accessToken, cb) => {
    const requestParams = {
      host,
      path: `/v2/payments/users/${ssoid}/cards`,
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    };

    httpRequest(requestParams, (res) => cb(res), null);
  },

  /**
   * POST Create a Stripe Token with Form ID
   *
   * Uses Stripe v3 createToken API. Reads data-stripe attributes from
   * form inputs to build the token payload (same DOM contract as v2).
   *
   * @param {String} formElementID - Form Element ID
   * @param {String} tokenType - "bank-account" or "card"
   * @param {Function} cb - Callback(status, response) matching v2 signature
   */
  createStripeToken: (formElementID, tokenType, cb) => {
    const formElement = document.getElementById(formElementID);
    if (!formElement) {
      return cb(400, { error: { message: "Form element not found: " + formElementID } });
    }
    const key = Stripe._publishableKey;
    if (!key) {
      console.error("createStripeToken: Stripe publishable key not set");
      return cb(400, { error: { message: "Stripe publishable key not set" } });
    }
    const stripe = Stripe(key);

    // Stripe v2 read data-stripe attributes from form inputs automatically.
    // v3 dropped that — it only accepts a plain object. So we scrape the
    // same attributes ourselves to keep the existing form markup working.
    const data = {};
    formElement.querySelectorAll("[data-stripe]").forEach((input) => {
      const attr = input.getAttribute("data-stripe");
      if (input.value) data[attr] = input.value;
    });

    const v3Type = "bank-account" === tokenType ? "bank_account" : "card";
    stripe.createToken(v3Type, data).then((result) => {
      if (result.error) {
        cb(result.error.type === "card_error" ? 402 : 400, {
          error: result.error,
        });
      } else {
        cb(200, result.token);
      }
    });
  },

  /**
   * POST Bank Data
   *
   * @function
   * @name updateBank
   * @param {String} - ssoid
   * @param {String} - accessToken
   * @param {String} - bankToken created from Stripe.js library
   * @param {Object} - payload object takes 'bank_account_token', 'legal_name', & 'type'
   * @param {Boolean} - isVerify is a flag to say whether or not this is an update to an existing bank account's tax info
   * @param {Function} - Callback
   */

  updateBank: (ssoid, accessToken, payload, cb) => {
    const requestParams = {
      host,
      path: `/v2/payments/users/${ssoid}/recipients`,
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    };

    httpRequest(requestParams, (res) => cb(res), payload);
  },

  /**
   * GET Bank Status Data
   *
   * @function
   * @name getBankStatusData
   * @param {Function} - Callback
   */

  getBankStatusData: (ssoid, accessToken, cb) => {
    const requestParams = {
      host,
      path: `/v2/payments/users/${ssoid}/bank/ready`,
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    };

    httpRequest(requestParams, (res) => cb(res), null);
  },

  /**
   * POST Card Data
   *
   * @function
   * @name addCard
   * @param {Function} - Callback
   */

  addCard: (ssoid, accessToken, cardToken, cb) => {
    const requestParams = {
      host,
      path: `/v2/payments/users/${ssoid}/cards/${cardToken}`,
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    };

    httpRequest(requestParams, (res) => cb(res), null);
  },

  /**
   * POST Upload ID
   *
   * @function
   * @name uploadId
   * @param {Function} - Callback
   */

  uploadId: (data, stripePublicKey, stripeAccountId, ssoId, token, cb) => {
    $.ajax({
      url: "https://uploads.stripe.com/v1/files",
      data,
      headers: {
        Authorization: "Bearer " + stripePublicKey,
        "Stripe-Account": stripeAccountId,
      },
      cache: false,
      contentType: false,
      processData: false,
      type: "POST",
    })
      .done((respdata) => {
        const payload = {
          individual: {
            verification: {
              document: {
                front: respdata.id,
              },
            },
          },
        };

        Payments.saveManagedAccountData(
          ssoId,
          token,
          payload,
          true,
          (response) => {
            if (200 !== response.statusCode) {
              return cb(true);
            }

            return cb(false, response);
          },
        );
      })
      .fail(() => {
        return cb(true);
      });
  },

  /**
   * GET Customer Identity Verification Session
   *
   * @function
   * @name getCustomerData
   * @param {Function} - Callback
   */

  getCustomerIdentitySession: (ssoid, accessToken, cb) => {
    const requestParams = {
      host,
      path: `/v2/payments/users/${ssoid}/verification`,
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    };

    httpRequest(requestParams, (res) => cb(res), null);
  },

  /**
   * GET Customer Identity Verification Session
   *
   * @function
   * @name getIdentitySession
   * @param ssoid - user ID
   * @param accessToken - accessToken
   * @param cb - callback function
   */

  getIdentitySession: (ssoid, accessToken, cb) => {
    const requestParams = {
      host,
      path: `/v2/payments/users/${ssoid}/verification`,
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    };

    httpRequest(requestParams, (res) => cb(res), null);
  },

  /**
   * POST Create Identity Verification Session
   *
   * @function
   * @name createIdentitySession
   * @param ssoid - user ID
   * @param accessToken - accessToken
   * @param payload - payload for creating idv session
   * @param cb - callback function
   */

  createIdentitySession: (ssoid, accessToken, payload, cb) => {
    const requestParams = {
      host,
      path: `/v2/payments/users/${ssoid}/verification`,
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    };

    httpRequest(requestParams, (res) => cb(res), payload);
  },
};

export default Payments;
